import * as Dialog from '@radix-ui/react-dialog'
import { usePreciosHubContext } from '@context/PreciosHubContext'
import { useAppStore } from '@store'
import atemporalLogoPresupuesto from '@assets/atemporal/AtemporalLogoPresupuesto.png'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { resolveSceneSeatHeightCmForDisplay } from '@utils/atemporal/seatHeightCatalog'
import {
  buildBudgetIllustrationsBlockHtml,
  fetchBudgetIllustrationsAsDataUrls,
  getCushionIllustrationSrc,
  getSeatHeightIllustrationSrc,
  getSeatStitchIllustrationSrc,
  type BudgetIllustrationPack,
} from '@utils/budget/presupuestoIllustrations'
import {
  getBudgetArtisanCharacteristics,
  buildArtisanCharacteristicsHtml,
} from '@utils/budget/artisanCharacteristics'
import {
  buildFabricCharacteristicsHtml,
  fetchFabricCareIconsAsDataUrls,
  pickSelectedFabricTexture,
  resolveFabricPanelLabels,
  type FabricCareIconId,
} from '@utils/budget/fabricCharacteristics'
import { formatBudgetPieceDimensions, formatArmBudgetReference, formatCatalogBaseReference } from '@utils/budget/formatPieceDimensions'
import { getRelaxIntegratedArmBudgetContribution } from '@utils/atemporal/relaxArmVariants'
import { captureSceneForBudget } from '@utils/camera/captureSceneForBudget'
import { FabricCharacteristicsBlock } from '@components/Budget/FabricCharacteristicsBlock'
import { ui } from '@utils/i18n/uiCopy'
import { uiDateLocale } from '@utils/i18n/uiLocale'
import { useEffect, useMemo, useRef, useState } from 'react'

/** Si imprimir o la descarga interrumpen el bloqueo del diálogo, la página se queda sin clics. */
function releaseBudgetInteractionLock() {
  if (document.querySelector('[role="dialog"]')) return
  const body = document.body
  const html = document.documentElement
  if (body.style.pointerEvents === 'none') body.style.pointerEvents = ''
  if (html.style.pointerEvents === 'none') html.style.pointerEvents = ''
  if (body.hasAttribute('data-scroll-locked')) {
    body.style.overflow = ''
    body.style.paddingRight = ''
    body.removeAttribute('data-scroll-locked')
  }
}

export function BudgetPreviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const items = useAppStore.use.items()
  const { resultadoPrecios } = usePreciosHubContext()
  const budgetContentRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const printFrameRef = useRef<HTMLIFrameElement | null>(null)
  const [capturedSceneImage, setCapturedSceneImage] = useState('')

  useEffect(() => {
    return () => {
      printFrameRef.current?.remove()
      printFrameRef.current = null
    }
  }, [])

  const wasOpenRef = useRef(open)
  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open
    if (!wasOpen || open) return
    const id = window.setTimeout(releaseBudgetInteractionLock, 0)
    return () => window.clearTimeout(id)
  }, [open])

  const normalizeBudgetReference = (item: any) => {
    const rawReference = String(item?.referencia || item?.catalogID || '-')
    if (item?.type !== 'linkedItem') return rawReference
    return formatCatalogBaseReference(rawReference, rawReference)
  }
  const isSeatLinkedSon = (item: any) =>
    item?.type === 'linkedSon' &&
    String(item?.catalogID || item?.referencia || '')
      .toLowerCase()
      .includes('asiento')
  const isArmLinkedItem = (item: any) => {
    if (item?.type !== 'linkedItem') return false
    const reference = String(item?.catalogID || item?.referencia || '').trim()
    return /^BR\d+(?:-\d+)?$/i.test(reference)
  }
  const getCushionBudgetLabel = (catalog: string) => {
    const normalized = String(catalog || '').toLowerCase()
    if (normalized.includes('cojinesr')) return ui('rectangularCushions')
    if (normalized.includes('cojin')) return ui('multiCushions')
    return ''
  }

  const budgetPieces = useMemo(
    () =>
      Array.from(items.values())
        .filter(
          (item: any) =>
            item?.type === 'linkedItem' && item?.universal === false && !isArmLinkedItem(item),
        )
        .map((item: any) => ({
          id: String(item?.id || ''),
          referencia: normalizeBudgetReference(item),
          thickness: Number(item?.thickness || 0),
          height: Number(item?.height || 0),
          width: Number(item?.width || 0),
        })),
    [items],
  )
  const budgetArms = useMemo(() => {
    const grouped = new Map<
      string,
      { id: string; count: number; thickness: number; height: number; width: number }
    >()

    const addArm = (
      armRef: string,
      count: number,
      dims?: { thickness?: number; height?: number; width?: number },
    ) => {
      const id = formatArmBudgetReference(armRef)
      if (!id || count <= 0) return
      const existing = grouped.get(id)
      if (existing) {
        existing.count += count
        return
      }
      grouped.set(id, {
        id,
        count,
        thickness: Number(dims?.thickness || 0),
        height: Number(dims?.height || 0),
        width: Number(dims?.width || 0),
      })
    }

    Array.from(items.values()).forEach((item: any) => {
      if (item?.type !== 'linkedItem' || item?.universal !== false) return

      if (isArmLinkedItem(item)) {
        addArm(String(item?.referencia || item?.catalogID || ''), 1, item)
        return
      }

      // Relax con BRx I/D → +1; BRx A → +2; sin brazo → nada.
      const relaxArms = getRelaxIntegratedArmBudgetContribution(item?.catalogID || item?.referencia)
      if (relaxArms) addArm(relaxArms.armRef, relaxArms.count)
    })

    return Array.from(grouped.values())
  }, [items])

  const seatHeightCm = useMemo(() => resolveSceneSeatHeightCmForDisplay(items), [items])
  const seatChildrenCount = useMemo(
    () =>
      Array.from(items.values()).filter((item: any) => {
        if (!isSeatLinkedSon(item)) return false
        return Boolean(item?.firstParentID)
      }).length,
    [items],
  )
  const seatStitchType = useMemo(() => {
    const source = Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && String(item?.seatStitchType || '').trim(),
    ) as any
    const stitch = String(source?.seatStitchType || '').trim().toLowerCase()
    return stitch === 'sin' ? 'sin' : 'con'
  }, [items])
  const seatCountLabel = seatChildrenCount === 1 ? ui('seatSingular') : ui('seatPlural')
  const seatStitchLabel = seatStitchType === 'sin' ? ui('withoutStitch') : ui('withStitch')
  const sceneCushionLabel = useMemo(() => {
    const cushionSon = Array.from(items.values()).find((item: any) => {
      if (item?.type !== 'linkedSon') return false
      const catalog = String(item?.catalogID || item?.referencia || '').toLowerCase()
      return catalog.includes('cojin')
    }) as any
    return getCushionBudgetLabel(String(cushionSon?.catalogID || cushionSon?.referencia || ''))
  }, [items])

  const selectedParentLinkedItem = useMemo(() => {
    const selectedLinkedParent = Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false && item?.selected,
    ) as any
    if (selectedLinkedParent) return selectedLinkedParent
    return Array.from(items.values()).find(
      (item: any) => item?.type === 'linkedItem' && item?.universal === false,
    ) as any
  }, [items])

  const selectedFabricTexture = useMemo(() => {
    const textures = Array.isArray(selectedParentLinkedItem?.textures)
      ? selectedParentLinkedItem.textures
      : []
    return pickSelectedFabricTexture(textures)
  }, [selectedParentLinkedItem])

  const fabricInfo = {
    acabado: String(selectedFabricTexture?.acabado || ''),
    name: String(selectedFabricTexture?.name || ''),
    imageUrl: String(selectedFabricTexture?.url || ''),
    miniUrl: String((selectedFabricTexture as any)?.urlMiniatura || ''),
  }

  const fabricLabels = useMemo(
    () => resolveFabricPanelLabels(selectedFabricTexture as any),
    [selectedFabricTexture],
  )
  const fabricCharacteristics = fabricLabels.profile
  const fabricMiniImageUrl = (() => {
    const zoneName = String((selectedFabricTexture as any)?.zoneName || '')
    const zoneMaterial = zoneName
      ? (selectedParentLinkedItem as any)?.zones?.[zoneName]?.material
      : null
    const miniName = String(zoneMaterial?.img || '').trim()
    const materialUrl = String(zoneMaterial?.url || fabricInfo.imageUrl || '').trim()
    if (!miniName || !materialUrl) return ''
    const lastSlash = materialUrl.lastIndexOf('/')
    if (lastSlash < 0) return ''
    const basePath = materialUrl.slice(0, lastSlash + 1)
    return `${basePath}${miniName}`
  })()
  const fabricSwatchSourceUrl = fabricInfo.miniUrl || fabricMiniImageUrl || fabricInfo.imageUrl
  const safeFabricImageUrl = fabricSwatchSourceUrl ? encodeURI(fabricSwatchSourceUrl) : ''
  // const formattedPrecioTotal =
  //   resultadoPrecios && typeof resultadoPrecios.precioTotal === 'number'
  //     ? `${new Intl.NumberFormat('es-ES').format(resultadoPrecios.precioTotal)} €`
  //     : '-'


  const getLogoDataUrl = async () => {
    try {
      const response = await fetch(atemporalLogoPresupuesto)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve(atemporalLogoPresupuesto)
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir logo'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return atemporalLogoPresupuesto
    }
  }

  const getImageDataUrl = async (imageUrl: string) => {
    if (!imageUrl) return ''
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            resolve('')
          }
        }
        reader.onerror = () => reject(new Error('No se pudo convertir imagen'))
        reader.readAsDataURL(blob)
      })
    } catch {
      return ''
    }
  }

  const buildBudgetHtml = (
    logoSrc: string,
    illus: BudgetIllustrationPack,
    fabricCareIconDataUrls: Partial<Record<FabricCareIconId, string>> = {},
  ) => {
    const today = new Date().toLocaleDateString(uiDateLocale())
    const escapeHtml = (value: string) =>
      value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')

    const imageBlock = capturedSceneImage
      ? `<img src="${capturedSceneImage}" alt="${escapeHtml(ui('currentScene'))}" style="display:block;margin:28px auto 0;max-width:520px;width:100%;height:auto;border-radius:12px;border:1px solid #d8d8d8;" />`
      : `<div style="height:240px;border:1px dashed #d8d8d8;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#6d6d6d;">${escapeHtml(ui('noCapture'))}</div>`
    const piecesHtml = budgetPieces
      .map(
        (piece) =>
          `<div class="line">${escapeHtml(ui('refPrefix'))} ${escapeHtml(piece.referencia)} ${formatBudgetPieceDimensions(piece)}</div>`,
      )
      .join('')
    const armsHtml = budgetArms
      .map(
        (arm) =>
          `<div class="line">${arm.count} ${
            arm.count === 1 ? ui('armSingular') : ui('armPlural')
          } - <span class="label-strong">Ref:</span> ${escapeHtml(arm.id)}</div>`,
      )
      .join('')
    const budgetIllustrationsHtml = buildBudgetIllustrationsBlockHtml(escapeHtml, {
      sceneCushionLabel: sceneCushionLabel || '',
      seatHeightCm,
      seatCountLabel,
      seatStitchLabel,
      illus,
    })
    const fabricCareHtml = fabricCharacteristics
      ? buildFabricCharacteristicsHtml(escapeHtml, fabricCharacteristics, fabricCareIconDataUrls)
      : ''
    const tejidoHtml =
      fabricLabels.group || fabricLabels.subgroup || fabricLabels.textureLabel || safeFabricImageUrl
      ? `<div class="line"><span class="label-strong">${escapeHtml(ui('fabric'))}:</span></div>
    <div class="fabric-head">
        ${
          safeFabricImageUrl
            ? `<span class="fabric-swatch" style="background-image:url('${safeFabricImageUrl}');"></span>`
            : `<span class="fabric-dot"></span>`
        }
        <div>
          ${fabricLabels.group ? `<div class="fabric-title">${escapeHtml(fabricLabels.group)}</div>` : ''}
          ${fabricLabels.subgroup ? `<div class="fabric-subtitle">${escapeHtml(fabricLabels.subgroup)}</div>` : ''}
          ${fabricLabels.textureLabel ? `<div class="fabric-code">${escapeHtml(fabricLabels.textureLabel)}</div>` : ''}
        </div>
    </div>
    ${fabricCareHtml}`
      : `<div class="line">${escapeHtml(ui('fabric'))} <span class="muted">${escapeHtml(ui('withPhoto'))}</span></div>`

    return `<!doctype html>
<html lang="${uiDateLocale() === 'es-ES' ? 'es' : 'en'}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(ui('budget'))}</title>
  <style>
    @page { size: A4; margin: 8mm; }
    @media print {
      html, body { margin: 0 !important; padding: 0 !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .budget-options-row { gap: 14px !important; }
      .budget-opt-card { max-width: 200px !important; min-width: 130px !important; flex: 1 1 150px !important; }
      .budget-opt-img-wrap { height: 140px !important; }
      .budget-opt-caption { font-size: 16px !important; margin-top: 8px !important; }
    }
    body { font-family: Arial, sans-serif; font-size: 16px; margin: 0; padding: 32px; color: #222; }
    .sheet { max-width: 840px; margin: 0 auto; }
    .logo { width: 220px; margin-bottom: 36px; }
    .line { margin-bottom: 14px; font-size: 16px; }
    .label-strong { font-weight: 700; }
    .muted { color: #8a8a8a; }
    .block { margin-top: 24px; }
    .total { margin-top: 22px; font-size: 16px; font-weight: 700; }
    .preview { margin-top: 28px; }
    .fabric-head { display: flex; align-items: center; gap: 14px; margin: 4px 0 12px; }
    .fabric-dot { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background: #b08f2f; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-swatch { display: inline-block; width: 200px; height: 80px; border-radius: 10px; margin-top: 0; background-size: cover; background-position: center; flex-shrink: 0; border: 1px solid #d4d4d4; }
    .fabric-title { font-size: 16px; font-weight: 700; margin-bottom: 2px; }
    .fabric-subtitle { font-size: 16px; color: #6d6d6d; }
    .fabric-code { font-size: 14px; color: #8a8a8a; margin-top: 2px; }
    .budget-characteristics-block { margin-bottom: 8px; }
    .budget-options-wrap { margin: 4px 0 24px; }
    .budget-options-row { display: flex; flex-wrap: wrap; gap: 24px; align-items: stretch; justify-content: flex-start; }
    .budget-opt-card { flex: 1 1 220px; max-width: 300px; min-width: 180px; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .budget-opt-img-wrap { width: 100%; height: 220px; display: block; position: relative; background: #f0f0f0; border-radius: 16px; border: 1px solid #cfcfcf; box-shadow: 0 6px 18px rgba(0,0,0,0.08); box-sizing: border-box; overflow: hidden; }
    .budget-opt-img-wrap img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
    .budget-opt-caption { margin-top: 14px; font-size: 16px; font-weight: 700; color: #1a1a1a; line-height: 1.35; }
    .characteristics-block { margin: 8px 0 20px; }
    .characteristics-text { margin: 0 0 4px; font-size: 16px; line-height: 1.35; color: #2a2a2a; }
    .fabric-care-block { margin: 10px 0 16px; }
    .fabric-care-title { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .fabric-care-group { font-size: 16px; font-weight: 700; margin: 4px 0 2px; color: #2a2a2a; }
    .fabric-care-desc { font-size: 16px; line-height: 1.45; color: #2a2a2a; margin-bottom: 8px; }
    .fabric-care-icons { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .fabric-care-icon { width: 28px; height: 28px; object-fit: contain; }
    .fabric-care-icon--slightly-small { width: 24px; height: 24px; }
    .fabric-care-icon--slightly-large { width: 32px; height: 32px; }
    .fabric-care-icon--medium { width: 36px; height: 36px; }
    .fabric-care-icon--large { width: 36px; height: 36px; }
  </style>
</head>
<body>
  <main class="sheet">
    <img class="logo" src="${logoSrc}" alt="Atemporal" />
    <div class="line"><span class="label-strong">${escapeHtml(ui('date'))}:</span> ${today}</div>
    <div class="line"><span class="label-strong">${escapeHtml(ui('model'))}:</span> Artisan</div>
    ${buildArtisanCharacteristicsHtml(escapeHtml)}
    <div class="line"><span class="label-strong">${escapeHtml(ui('reference'))}:</span></div>
    ${piecesHtml}
    ${budgetArms.length > 0 ? `<div class="line"><span class="label-strong">${escapeHtml(ui('arms'))}:</span></div>${armsHtml}` : ''}
    ${budgetIllustrationsHtml}
    ${tejidoHtml}
    <div class="preview">${imageBlock}</div>
   
  </main>
</body>
</html>`
  }

  const captureSceneWithOverlays = async () => {
    const storeBeforeCapture = useAppStore.getState()
    const previousHideDimensions = storeBeforeCapture.hideDimentions3D ?? false
    const previousCurrentLayer = storeBeforeCapture.currentLayer
    const previousCurrentObjectId = storeBeforeCapture.currentObjectID
    const previousSelected = Array.from(storeBeforeCapture.layers[previousCurrentLayer]?.selected || [])
    const itemsForCapture = storeBeforeCapture.items

    useAppStore.setState((state) => {
      state.hideDimentions3D = true
      state.currentObjectID = ''
      state.layers[state.currentLayer]?.selected?.clear()
    })

    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => setTimeout(resolve, 80))

    try {
      // Cámara dedicada: no muta CameraControls ni la PerspectiveCamera de trabajo.
      const fullCaptureData = captureSceneForBudget(itemsForCapture)
      if (fullCaptureData.length > 100) {
        return fullCaptureData
      }
    } catch (error) {
      console.error('No se pudo capturar escena para presupuesto:', error)
    } finally {
      useAppStore.setState((state) => {
        state.hideDimentions3D = previousHideDimensions
        state.currentObjectID = previousCurrentObjectId
        if (state.layers[previousCurrentLayer]) {
          state.layers[previousCurrentLayer].selected.clear()
          previousSelected.forEach((id) => state.layers[previousCurrentLayer].selected.add(id))
        }
      })
    }
    return ''
  }

  useEffect(() => {
    if (!open) return
    const capture = async () => {
      const imageData = await captureSceneWithOverlays()
      setCapturedSceneImage(imageData)
    }
    capture()
  }, [open])

  const handleDownloadBudget = async () => {
    const target = budgetContentRef.current
    if (!target) return

    const cloneWrapper = document.createElement('div')
    cloneWrapper.style.position = 'fixed'
    cloneWrapper.style.left = '-100000px'
    cloneWrapper.style.top = '0'
    cloneWrapper.style.width = '1024px'
    cloneWrapper.style.background = '#ffffff'
    cloneWrapper.style.padding = '24px'
    cloneWrapper.style.zIndex = '-1'

    const clonedContent = target.cloneNode(true) as HTMLElement
    const clonedActions = clonedContent.querySelector('[data-budget-actions]') as HTMLElement | null
    if (clonedActions) clonedActions.style.display = 'none'
    const clonedFabricSwatchWrap = clonedContent.querySelector(
      '[data-fabric-swatch-wrap]',
    ) as HTMLElement | null
    if (clonedFabricSwatchWrap) clonedFabricSwatchWrap.style.display = 'none'

    const clonedFabricSwatchImg = clonedContent.querySelector(
      '[data-fabric-swatch-img]',
    ) as HTMLImageElement | null
    if (clonedFabricSwatchImg && safeFabricImageUrl) {
      const fabricDataUrl = await getImageDataUrl(safeFabricImageUrl)
      if (fabricDataUrl) {
        clonedFabricSwatchImg.src = fabricDataUrl
      }
    }

    const budgetOptionImgs = clonedContent.querySelectorAll(
      '[data-budget-option-row] img',
    ) as NodeListOf<HTMLImageElement>
    await Promise.all(
      Array.from(budgetOptionImgs).map(async (img) => {
        const src = img.getAttribute('src')
        if (!src || src.startsWith('data:')) return
        const dataUrl = await getImageDataUrl(src)
        if (dataUrl) img.src = dataUrl
      }),
    )

    cloneWrapper.appendChild(clonedContent)
    document.body.appendChild(cloneWrapper)

    try {
      const clonedImages = Array.from(cloneWrapper.querySelectorAll('img')) as HTMLImageElement[]
      await Promise.all(
        clonedImages.map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve()
                return
              }
              img.onload = () => resolve()
              img.onerror = () => resolve()
            }),
        ),
      )

      const captureCanvas = await html2canvas(cloneWrapper, {
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 2),
      })

      const imgData = captureCanvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 8
      const usableWidth = pageWidth - margin * 2
      const usableHeight = pageHeight - margin * 2
      const widthScale = usableWidth / captureCanvas.width
      const heightScale = usableHeight / captureCanvas.height
      const scale = Math.min(widthScale, heightScale)
      const renderWidth = captureCanvas.width * scale
      const renderHeight = captureCanvas.height * scale
      const offsetX = margin + (usableWidth - renderWidth) / 2
      const offsetY = margin

      pdf.addImage(imgData, 'PNG', offsetX, offsetY, renderWidth, renderHeight)
      pdf.save(`presupuesto-${new Date().toISOString().slice(0, 10)}.pdf`)
    } finally {
      cloneWrapper.remove()
      // jsPDF pulsa un enlace fuera del diálogo y html2canvas mueve el foco.
      // Sin devolverlo, el cierre del modal deja de recibir clics.
      requestAnimationFrame(() => closeButtonRef.current?.focus({ preventScroll: true }))
    }
  }

  const handlePrintBudget = async () => {
    const logoSrc = await getLogoDataUrl()
    const illus = await fetchBudgetIllustrationsAsDataUrls({
      sceneCushionLabel: sceneCushionLabel || '',
      seatHeightCm,
      seatStitchType,
    })
    const fabricCareIcons = await fetchFabricCareIconsAsDataUrls(fabricCharacteristics)
    const html = buildBudgetHtml(logoSrc, illus, fabricCareIcons)

    printFrameRef.current?.remove()

    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.visibility = 'hidden'
    printFrameRef.current = iframe
    document.body.appendChild(iframe)

    const frameWindow = iframe.contentWindow
    if (!frameWindow) {
      iframe.remove()
      printFrameRef.current = null
      return
    }

    // Quitar el iframe a los 1,2 s, con el diálogo de impresión aún abierto,
    // deja el bloqueo de scroll/foco de Radix activo: el fondo no cierra y
    // no hay otro control dentro de la hoja.
    let cleaned = false
    let fallbackId = 0
    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      window.clearTimeout(fallbackId)
      window.removeEventListener('afterprint', cleanup)
      frameWindow.removeEventListener('afterprint', cleanup)
      iframe.remove()
      if (printFrameRef.current === iframe) printFrameRef.current = null
      requestAnimationFrame(() => closeButtonRef.current?.focus({ preventScroll: true }))
    }

    fallbackId = window.setTimeout(cleanup, 120_000)
    window.addEventListener('afterprint', cleanup)
    frameWindow.addEventListener('afterprint', cleanup)

    iframe.onload = () => {
      try {
        frameWindow.focus()
        frameWindow.print()
      } catch {
        cleanup()
      }
    }
    iframe.srcdoc = html
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className='fixed inset-0 z-[100] bg-black/70'
          style={{ pointerEvents: 'auto' }}
        />
        <Dialog.Content
          className='fixed left-1/2 top-1/2 z-[101] flex max-h-[90vh] w-[92vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'
          style={{ pointerEvents: 'auto' }}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            closeButtonRef.current?.focus({ preventScroll: true })
          }}
        >
          <Dialog.Title className='sr-only'>{ui('budget')}</Dialog.Title>
          <Dialog.Close asChild>
            <button
              ref={closeButtonRef}
              type='button'
              aria-label={ui('closeBudget')}
              onClick={onClose}
              className='absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full text-[#a39880] transition-colors hover:bg-[#f6f1e4] hover:text-colorAtemporal focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-colorAtemporal'
            >
              <svg
                width='18'
                height='18'
                viewBox='0 0 12 12'
                fill='none'
                aria-hidden='true'
              >
                <path
                  d='M2.25 2.25l7.5 7.5M9.75 2.25l-7.5 7.5'
                  stroke='currentColor'
                  strokeWidth='1.15'
                  strokeLinecap='round'
                />
              </svg>
            </button>
          </Dialog.Close>
          <div className='min-h-0 flex-1 overflow-auto px-4 pb-4 pt-12 min-[768px]:px-6 min-[768px]:pb-6'>
          <div
            ref={budgetContentRef}
            data-budget-root
            className='mx-auto max-w-3xl'
            style={{ fontFamily: 'Arial, sans-serif' }}
          >
            <img
              src={atemporalLogoPresupuesto}
              alt='Atemporal'
              className='mb-8 w-44'
            />
            <div
              data-budget-text
              className='space-y-2 text-[#2A2A2A]'
            >
              <p>
                <span className='font-bold'>{ui('date')}:</span> {new Date().toLocaleDateString(uiDateLocale())}
              </p>
              <p>
                <span className='font-bold'>{ui('model')}:</span> Artisan
              </p>
              <div className='mt-2'>
                <p>
                  <span className='font-bold'>{ui('characteristics')}:</span>
                </p>
                <div className='mt-1 space-y-1 leading-snug'>
                  {getBudgetArtisanCharacteristics().map((text) => (
                    <p key={text}>{text}</p>
                  ))}
                </div>
              </div>
              <p>
                <span className='font-bold'>{ui('reference')}:</span>
              </p>
              {budgetPieces.map((piece) => (
                <p key={piece.id}>
                  {ui('refPrefix')} {piece.referencia} {formatBudgetPieceDimensions(piece)}
                </p>
              ))}
              {budgetArms.length > 0 && (
                <>
                  <p>
                    <span className='font-bold'>{ui('arms')}:</span>
                  </p>
                  {budgetArms.map((arm) => (
                    <p key={`arm-${arm.id}`}>
                      {arm.count} {arm.count === 1 ? ui('armSingular') : ui('armPlural')} -{' '}
                      <span className='font-bold'>Ref:</span> {arm.id}
                    </p>
                  ))}
                </>
              )}
              {(getCushionIllustrationSrc(sceneCushionLabel || '') ||
                getSeatHeightIllustrationSrc(seatHeightCm) ||
                (seatHeightCm !== null && getSeatStitchIllustrationSrc(seatStitchType))) && (
                <div data-budget-characteristics className='mt-2'>
                  <p className='text-[#2A2A2A]'>
                    <span className='font-bold'>{ui('options')}:</span>
                  </p>
                  <div
                    data-budget-option-row
                    className='mt-3 flex flex-wrap items-stretch justify-start gap-6'
                  >
                    {getCushionIllustrationSrc(sceneCushionLabel || '') ? (
                      <div className='flex max-w-[min(300px,100%)] min-w-[180px] flex-1 flex-col items-center text-center'>
                        <div className='relative h-[200px] w-full overflow-hidden rounded-2xl border border-[#cfcfcf] bg-[#f0f0f0] shadow-md'>
                          <img
                            src={getCushionIllustrationSrc(sceneCushionLabel || '')}
                            alt={sceneCushionLabel || ui('cushions')}
                            className='absolute inset-0 h-full w-full object-cover object-center'
                          />
                        </div>
                        <p className='mt-3 font-bold text-[#1a1a1a]'>{sceneCushionLabel}</p>
                      </div>
                    ) : null}
                    {getSeatHeightIllustrationSrc(seatHeightCm) ? (
                      <div className='flex max-w-[min(300px,100%)] min-w-[180px] flex-1 flex-col items-center text-center'>
                        <div className='relative h-[200px] w-full overflow-hidden rounded-2xl border border-[#cfcfcf] bg-[#f0f0f0] shadow-md'>
                          <img
                            src={getSeatHeightIllustrationSrc(seatHeightCm)}
                            alt={`${ui('cushionHeight')} ${seatHeightCm} cm`}
                            className='absolute inset-0 h-full w-full object-cover object-center'
                          />
                        </div>
                        <p className='mt-3 font-bold text-[#1a1a1a]'>{ui('cushionHeight')}: {seatHeightCm} cm</p>
                      </div>
                    ) : null}
                    {seatHeightCm !== null ? (
                      <div className='flex max-w-[min(300px,100%)] min-w-[180px] flex-1 flex-col items-center text-center'>
                        <div className='relative h-[200px] w-full overflow-hidden rounded-2xl border border-[#cfcfcf] bg-[#f0f0f0] shadow-md'>
                          <img
                            src={getSeatStitchIllustrationSrc(seatStitchType)}
                            alt={seatStitchLabel}
                            className='absolute inset-0 h-full w-full object-cover object-center'
                          />
                        </div>
                        <p className='mt-3 font-bold text-[#1a1a1a]'>
                          {seatCountLabel} {seatStitchLabel}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
              {(fabricLabels.group ||
                fabricLabels.subgroup ||
                fabricLabels.textureLabel ||
                safeFabricImageUrl) && (
                <div>
                  <p>
                    <span className='font-bold'>{ui('fabric')}:</span>
                  </p>
                    <div className='mb-2 mt-1 flex items-center gap-3.5'>
                    {safeFabricImageUrl ? (
                      <span
                        data-fabric-swatch-wrap
                        className='inline-block h-[80px] w-[200px] overflow-hidden rounded-[10px] border border-[#D4D4D4]'
                      >
                        <img
                          src={safeFabricImageUrl}
                          alt=''
                          data-fabric-swatch-img
                          className='h-full w-full object-cover'
                          style={{ imageRendering: 'auto' }}
                        />
                      </span>
                    ) : (
                      <span
                        data-fabric-swatch-wrap
                        className='inline-block h-[80px] w-[200px] rounded-[10px] border border-[#D4D4D4] bg-[#b08f2f]'
                      />
                    )}
                    <div className='pl-1'>
                      {fabricLabels.group ? (
                        <p className='font-semibold text-[#2A2A2A]'>{fabricLabels.group}</p>
                      ) : null}
                      {fabricLabels.subgroup ? (
                        <p className='text-[#555]'>{fabricLabels.subgroup}</p>
                      ) : null}
                      {fabricLabels.textureLabel ? (
                        <p className='text-[#777]'>{fabricLabels.textureLabel}</p>
                      ) : null}
                    </div>
                  </div>
                  {fabricCharacteristics ? (
                    <FabricCharacteristicsBlock profile={fabricCharacteristics} />
                  ) : null}
                </div>
              )}
            </div>
            <div className='mt-8'>
              {capturedSceneImage ? (
                <img
                  src={capturedSceneImage}
                  alt={ui('currentScene')}
                  className='mx-auto mt-2 h-auto w-full max-w-[520px] rounded-xl border border-[#DCDCDC]'
                />
              ) : null}
            </div>
            {/* <p className='mt-7 text-2xl font-semibold text-[#1f1f1f]'>Precio Total: {formattedPrecioTotal}</p> */}
            <div
              data-budget-actions
              className='mt-8 flex flex-wrap justify-end gap-3'
            >
              <button
                onClick={handlePrintBudget}
                className='rounded-xl border border-colorAtemporal px-4 py-2 text-sm font-medium text-colorAtemporal transition-colors hover:bg-[#f5f5f5]'
              >
                {ui('print')}
              </button>
              <button
                onClick={handleDownloadBudget}
                className='rounded-xl bg-colorAtemporal px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90'
              >
                {ui('download')}
              </button>
            </div>
          </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
